import React from "react";
import { filter, includes, isEmpty, map } from "lodash";
import { Facet } from "@linkfluence/models/facet";
import { Locale } from "@linkfluence/models/locale";
import Selector from "@radarly/components/panels/card-edition/attribute-selector";
import { useFacetsFromContext } from "@radarly/core/facets/use-cases";
import { useFeature } from "@radarly/core/features/use-cases";
import { useCurrentProjectFocusesId } from "@radarly/core/project/use-cases";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { useCurrentWorkspaceId } from "@radarly/core/workspace/use-cases";
import { tr } from "@radarly/i18n";
import { Card, CardWithTerm } from "@radarly/models/cards";
import {
  editDatavizOptionsOnCard,
  switchToDateMetricCard,
  switchToDemographicCard,
} from "@radarly/models/cards/editor";
import { setCardTerm } from "@radarly/models/cards/editor";
import {
  changeTermSelectionMethod,
  hasTwoAttributeFields,
} from "@radarly/models/cards/form";
import {
  isDateMetricCard,
  isDateMetricsCard,
  isDemographicCard,
  isHiddenTermsCard,
} from "@radarly/models/cards/is";
import {
  CardTerm,
  demographicId,
  getTermFromCard,
  globalVolumeAndKPIId,
  isTermCompatibleForCard,
} from "@radarly/models/cards/term";
import { isMultipleLineChartDataviz } from "@radarly/models/dataviz";
import { categories } from "./models";

interface SharedPanelsCardEditionSidebarProps {
  card: Card;
  setCard: (card: Card) => void;
  closePanel: () => void;
}

type FacetValue = Facet | "__demographic__" | "__global-volume-and-kpi__";

const SharedPanelsCardEditionSidebar = ({
  card,
  setCard,
  closePanel,
}: SharedPanelsCardEditionSidebarProps) => {
  const imageRecognitionEnabled = useFeature("image_recognition");
  const facets = useFacetsFromContext();
  const cardTerm = getTermFromCard(card);
  const term = changeTermSelectionMethod(cardTerm, "frequency");
  const locale = useCurrentUserLocale();
  const workspaceId = useCurrentWorkspaceId();
  const projectFocuses = useCurrentProjectFocusesId();

  const getFullValue = (facet: FacetValue) => {
    const fullFacet = facets[facet];
    return {
      value: facet,
      label: fullFacet
        ? fullFacet.label
        : facet === demographicId
        ? tr(locale, `CUSTOM_PAGES::EDITOR::DEMOGRAPHICS`)
        : tr(locale, `CUSTOM_PAGES::EDITOR::GLOBAL_VOLUME_AND_KPIS`),
      compatible:
        (isDemographicCard(card) && facet === demographicId) ||
        ((isDateMetricCard(card) || isDateMetricsCard(card)) &&
          facet === globalVolumeAndKPIId) ||
        isTermCompatibleForCard(card, facet as Facet, fullFacet),
    };
  };

  const getCustomFields = () => {
    return map(filter(facets, { custom: true }), (facet) => ({
      value: facet.id,
      label: facet.label,
      compatible: isTermCompatibleForCard(card, facet.id as Facet, facet),
    }));
  };
  const getSections = (locale: Locale) => {
    const hiddenEmptyFacets = imageRecognitionEnabled
      ? [
          Facet.ImageRecognitionLogos,
          Facet.ImageRecognitionPersonIdentifications,
        ]
      : [];
    return [
      ...map(categories, (category) => ({
        title: tr(locale, category.title),
        values: category.values
          .filter(
            (facet) =>
              !includes(hiddenEmptyFacets, facet) ||
              !isEmpty(facets[facet].values)
          )
          .map((facet) => getFullValue(facet as FacetValue)),
      })),
      {
        title: tr(locale, "FACETS::SECTIONS::CUSTOM_FIELDS"),
        values: getCustomFields(),
      },
    ];
  };

  const sections = getSections(locale);

  return (
    <Selector
      title={
        hasTwoAttributeFields(card)
          ? tr(locale, "CUSTOM_PAGES::EDITOR::FIRST_ANALYZED_ATTRIBUTE")
          : tr(locale, "CUSTOM_PAGES::EDITOR::ANALYZED_ATTRIBUTE")
      }
      searchPlaceholder={tr(locale, "CUSTOM_PAGES::EDITOR::SEARCH_PLACEHOLDER")}
      sections={sections}
      selected={term}
      onClickBack={closePanel}
      locale={locale}
      onSelect={(value) => {
        if (value === globalVolumeAndKPIId) {
          setCard(switchToDateMetricCard(workspaceId, projectFocuses, card));
        } else if (value === demographicId) {
          setCard(switchToDemographicCard(workspaceId, projectFocuses, card));
        } else if (isMultipleLineChartDataviz(card.dataviz)) {
          const cleanCard = editDatavizOptionsOnCard(card, {
            ...card.dataviz,
            filteredValues: [],
          });
          setCard(
            setCardTerm(
              workspaceId,
              projectFocuses,
              cleanCard,
              value as CardTerm,
              facets
            )
          );
        } else if (isHiddenTermsCard(card)) {
          const cleanCard = editDatavizOptionsOnCard(card, {
            ...card.dataviz,
            hiddenTerms: [],
          });
          setCard(
            setCardTerm(
              workspaceId,
              projectFocuses,
              cleanCard,
              value as CardTerm,
              facets
            )
          );
        } else {
          setCard(
            setCardTerm(
              workspaceId,
              projectFocuses,
              card as CardWithTerm,
              value as CardTerm,
              facets
            )
          );
        }
      }}
    />
  );
};

export default SharedPanelsCardEditionSidebar;
